import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBlocker, useLocation, useNavigate } from 'react-router'

import { useLatestRef } from '@shared/hooks/src/useLatestRef'

import { logEvent } from '@utils/GTM'

import LeaveDialog from './LeaveDialog'
import { LeavePromptContext, resolveWhen } from './LeavePrompt.hooks'

const HISTORY_KEY = 'leave-prompt-history'

const readEntries = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY)) || {}
  } catch {
    return {}
  }
}

const createRegistry = () => {
  let guards = []

  return {
    register: (id, ref) => {
      guards = [...guards, { id, ref }]
    },
    unregister: (id) => {
      guards = guards.filter((guard) => guard.id !== id)
    },
    active: (predicate = () => true) => guards.map(({ ref }) => ref.current).filter((guard) => predicate(guard) && resolveWhen(guard.when)),
  }
}

/**
 * Owns the single router blocker, the `beforeunload` handler and the confirmation dialog that
 * every `useLeavePrompt` guard shares. React Router honors one blocker per router, so guards
 * register here instead of each holding their own.
 *
 * The router blocker only acts on in-app pushes and replaces. History pops are never blocked:
 * blocking one means the router undoing it with `history.go()` and redoing it later, which
 * WebKit does not handle reliably and which leaves the dialog open over the wrong page. The
 * app's own back controls still prompt, by asking `intercept` before they pop: the pathname
 * behind each history index is recorded as the patient moves, so the destination of a step
 * back is known up front and the dialog opens before anything navigates. The browser's back
 * and forward buttons stay plain navigation.
 *
 * A navigation whose state carries `skipLeavePrompt` is never blocked, for completion and error
 * paths that leave a flow on purpose.
 */
export default function LeavePromptProvider({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useLatestRef(location)

  const [registry] = useState(createRegistry)
  const [prompt, setPrompt] = useState(null)
  const [open, setOpen] = useState(false)

  const ask = useCallback((guard, proceed, cancel) => {
    logEvent('leave_prompt_shown', { source: guard.source })
    setPrompt({ guard, proceed, cancel })
    setOpen(true)
  }, [])

  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    if (historyAction === 'POP' || nextLocation.state?.skipLeavePrompt) return false
    if (nextLocation.pathname === currentLocation.pathname) return false

    const guard = registry.active((candidate) => candidate.blocksNavigation).at(-1)
    if (!guard || guard.allow?.(nextLocation, currentLocation)) return false

    ask(guard)
    return true
  })

  useEffect(() => {
    const index = window.history.state?.idx
    if (typeof index !== 'number') return
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify({ ...readEntries(), [index]: location.pathname }))
  }, [location])

  const intercept = useCallback(
    (delta) => {
      const guard = registry.active((candidate) => candidate.blocksNavigation).at(-1)
      if (!guard) return false

      const current = locationRef.current
      const pathname = readEntries()[window.history.state?.idx + delta]
      if (pathname && (pathname === current.pathname || guard.allow?.({ pathname }, current))) return false

      ask(guard, () => navigate(delta))
      return true
    },
    [registry, locationRef, ask, navigate]
  )

  useEffect(() => {
    const handler = (event) => {
      if (registry.active().length === 0) return
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [registry])

  const onStay = useCallback(() => {
    logEvent('leave_prompt_stayed', { source: prompt.guard.source })
    setOpen(false)
    if (!prompt.proceed) blocker.reset()
  }, [prompt, blocker])

  const onLeave = useCallback(() => {
    logEvent('leave_prompt_left', { source: prompt.guard.source })
    setOpen(false)
    if (prompt.proceed) prompt.proceed()
    else blocker.proceed()
  }, [prompt, blocker])

  const onExited = useCallback(() => setPrompt(null), [])

  const value = useMemo(() => ({ ...registry, intercept }), [registry, intercept])

  return (
    <LeavePromptContext.Provider value={value}>
      {children}
      {prompt && (
        <LeaveDialog
          open={open}
          source={prompt.guard.source}
          icon={prompt.guard.icon}
          onStay={onStay}
          onLeave={onLeave}
          onExited={onExited}
        />
      )}
    </LeavePromptContext.Provider>
  )
}
